import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { IoCreateOutline, IoEyeOutline } from "react-icons/io5";
import { getNodeServiceBaseUrl } from "../../../../config/apiBase";
import ModulePage, { ModuleToolbar } from "../../../../shared-ui/ModulePage";
import { EmptyState, ErrorState, Skeleton } from "../../../../shared-ui/States";
import { formatDate } from "../../../../utils/fn";
import "../../../../shared-ui/crane-tokens.css";
import "../../../../shared-ui/RowActions.css";

function usePermissions() {
  return useMemo(() => {
    try {
      const raw = localStorage.getItem("zTaskUserDetails");
      if (!raw) return { user: null, permissions: [] };
      const userDetails = JSON.parse(raw);
      const permissions = userDetails?.role?.permissions
        ? userDetails.role.permissions.map((p) => p.title)
        : [];
      return { user: userDetails, permissions };
    } catch {
      return { user: null, permissions: [] };
    }
  }, []);
}

export default function StitchWorkOrdersPage() {
  const { user, permissions } = usePermissions();
  const [workOrders, setWorkOrders] = useState([]);
  const [customerMap, setCustomerMap] = useState({});
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");

  const hasPermission = useCallback(
    (name) => {
      if (Array.isArray(name)) return name.some((n) => permissions.includes(n));
      return permissions.includes(name);
    },
    [permissions]
  );

  const canEditWorkOrder = useCallback(
    (workOrder) => {
      if (hasPermission("EDIT ALL WORK ORDERS")) return true;
      if (user?.id === workOrder.added_by) return true;
      return false;
    },
    [user, hasPermission]
  );

  const load = useCallback(async () => {
    if (!user) {
      setErr("User details not found. Please log in again.");
      setBusy(false);
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const token = localStorage.getItem("zTaskAuthToken");
      const headers = { Authorization: `Bearer ${token}`, Accept: "application/json" };
      const woUrl = `${getNodeServiceBaseUrl()}/work-order/api/work-orders`;
      const custUrl = `${getNodeServiceBaseUrl()}/customer/api/customers/`;

      const [woRes, custRes] = await Promise.all([
        fetch(woUrl, { headers }),
        fetch(custUrl, { headers }),
      ]);
      if (!woRes.ok) throw new Error("Failed to fetch work orders.");
      const woData = await woRes.json();
      const customers = custRes.ok ? await custRes.json() : [];
      const cmap = {};
      (Array.isArray(customers) ? customers : []).forEach((c) => {
        cmap[c.id] = c.name;
        cmap[String(c.id)] = c.name;
      });
      setCustomerMap(cmap);

      let rows = Array.isArray(woData) ? woData : [];
      if (!hasPermission("VIEW ALL WORK ORDERS")) {
        rows = rows.filter((wo) => wo.added_by === user.id);
      }
      rows.sort((a, b) => Number(b.id) - Number(a.id));
      setWorkOrders(rows);
    } catch (e) {
      setErr(e.message || "Failed to load work orders");
      setWorkOrders([]);
    } finally {
      setBusy(false);
    }
  }, [user, hasPermission]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return workOrders;
    return workOrders.filter((wo) => {
      const customer = customerMap[wo.cust_id] || "";
      const hay = [
        wo.wo_no,
        wo.id,
        wo.wo_open_close,
        customer,
        wo.wo_date,
        wo.add_date,
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [workOrders, query, customerMap]);

  return (
    <ModulePage
      crumbs={["Operations", "ZOrder", "Work orders"]}
      title="Work orders"
      description="Live work orders from the Node work-order service."
      actions={
        <>
          <button type="button" className="crane-btn" onClick={load} disabled={busy}>
            Refresh
          </button>
          {hasPermission("CREATE WORK ORDER") && (
            <Link to="/work-orders/new" className="crane-btn crane-btn--primary" style={{ textDecoration: "none" }}>
              + New work order
            </Link>
          )}
        </>
      }
    >
      <ModuleToolbar>
        <input
          type="search"
          className="crane-input"
          placeholder="Search WO#, customer, status…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Filter work orders"
          style={{ maxWidth: 360 }}
        />
      </ModuleToolbar>

      {busy && <Skeleton rows={6} height={40} />}
      {!busy && err && <ErrorState title="Could not load work orders" description={err} onRetry={load} />}
      {!busy && !err && filtered.length === 0 && (
        <EmptyState
          title={query ? "No matching work orders" : "No work orders yet"}
          description={query ? "Try another search." : "Create a work order when you are ready to bill a job."}
          action={
            !query && hasPermission("CREATE WORK ORDER") ? (
              <Link to="/work-orders/new" className="crane-btn crane-btn--primary" style={{ textDecoration: "none" }}>
                New work order
              </Link>
            ) : null
          }
        />
      )}
      {!busy && !err && filtered.length > 0 && (
        <div className="crane-card" style={{ overflowX: "auto" }}>
          <table className="crane-table">
            <thead>
              <tr>
                <th>WO #</th>
                <th>Customer</th>
                <th>Status</th>
                <th>WO date</th>
                <th>Created</th>
                <th>Updated</th>
                <th style={{ textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((wo) => {
                const canEdit = canEditWorkOrder(wo);
                return (
                  <tr key={wo.id}>
                    <td>
                      <Link to={`/work-orders/${wo.id}`}>{wo.wo_no || wo.id}</Link>
                    </td>
                    <td>{customerMap[wo.cust_id] || "—"}</td>
                    <td>{wo.wo_open_close || "—"}</td>
                    <td>{wo.wo_date ? formatDate(wo.wo_date) : "—"}</td>
                    <td>{wo.add_date ? formatDate(wo.add_date) : "—"}</td>
                    <td>{wo.update_date ? formatDate(wo.update_date) : "—"}</td>
                    <td style={{ textAlign: "center" }}>
                      <div className="crane-row-actions">
                        {hasPermission(["VIEW WORK ORDER", "VIEW ALL WORK ORDERS"]) && (
                          <NavLink
                            to={`/work-orders/${wo.id}`}
                            className="crane-row-action crane-row-action--view"
                            title="View"
                            aria-label="View work order"
                          >
                            <IoEyeOutline size={16} />
                          </NavLink>
                        )}
                        {hasPermission("SET WORK ORDER") && (
                          <NavLink
                            to={`/work-orders/${wo.id}/edit`}
                            className={`crane-row-action crane-row-action--edit${canEdit ? "" : " is-disabled"}`}
                            title={canEdit ? "Edit" : "You cannot edit this work order"}
                            aria-label="Edit work order"
                            style={{ pointerEvents: canEdit ? "auto" : "none", opacity: canEdit ? 1 : 0.4 }}
                            onClick={(e) => {
                              if (!canEdit) e.preventDefault();
                            }}
                          >
                            <IoCreateOutline size={16} />
                          </NavLink>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </ModulePage>
  );
}
